import { createSelector } from 'reselect';
import {
  prop,
  props,
  filter,
  isNil,
  identity,
  propEq,
  mergeRight,
  propOr,
  map,
  sortWith,
  ascend,
  values,
  reject,
  __,
  pipe,
  mergeAll,
} from 'ramda';
import {
  selectSortedExperiencesArray,
  selectMarkets,
  selectIsFetchingMarketExperiences,
  selectRegions,
  selectCategories,
} from '../contentRegistry';

import { ROUTES } from '../../routes/routes';

export const selectMarketDomain = prop('market');

export const selectCurrentMarketId = createSelector(
  selectMarketDomain,
  prop('currentMarketId')
);

export const selectCurrentMarketData = createSelector(
  selectCurrentMarketId,
  selectMarkets,
  prop
);

export const selectIsFetchingCurrentMarketExperiences = createSelector(
  selectCurrentMarketId,
  selectIsFetchingMarketExperiences,
  prop
);

export const selectCurrentMarketName = createSelector(
  selectCurrentMarketData,
  prop('name')
);

export const selectCurrentMarketTimezone = createSelector(
  selectCurrentMarketData,
  prop('timeZone')
);

export const selectCurrentMarketExperiences = createSelector(
  selectSortedExperiencesArray,
  selectCurrentMarketId,
  (experiences, currentMarket) => {
    const filterFn = isNil(currentMarket) ? identity : filter(propEq('market', currentMarket));
    return filterFn(experiences);
  }
);

export const selectCurrentMarketSlug = createSelector(
  selectCurrentMarketData,
  prop('slug')
);

export const selectCurrentMarketHomeRoute = createSelector(
  selectCurrentMarketSlug,
  slug => ROUTES.market(slug)
);

export const selectAllMarketsSuggestedExperiences = createSelector(
  selectMarketDomain,
  prop('suggestedMarketExperiences')
);

export const selectMarketExperiencesTotal = createSelector(
  selectMarketDomain,
  prop('marketExperiencesTotal')
);

export const selectMarketsAlphabetical = createSelector(
  selectMarkets,
  markets => sortWith([ascend(prop('name'))], values(markets))
);

export const selectMarketsWithRegions = createSelector(
  selectMarketsAlphabetical,
  selectRegions,
  (markets, regions) => {
    const regionsWithoutDrafts = pipe(
      propOr([], 'regions'),
      props(__, regions),
      reject(isNil)
    );

    return Object.values(map(market => mergeRight(market, { regions: regionsWithoutDrafts(market) }), markets));
  }
);

export const selectMarketsCategories = createSelector(
  selectMarketsAlphabetical,
  selectCategories,
  (markets, allCategories) =>
    pipe(
      map(({ id, categories = [] }) => ({
        [id]: map(id => allCategories[id])(categories),
      })),
      mergeAll
    )(markets)
);

export const selectMarketFromProps = (state, props) => props.market;

export const selectMarketSuggestedExperiences = createSelector(
  selectAllMarketsSuggestedExperiences,
  selectMarketFromProps,
  (suggestedExperiences, market) => suggestedExperiences[market] || []
);
